import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button as MuiButton, Stack } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { InputTypeahead } from '@farmshare/ui-components';
import { useCustomerOptions } from 'features/customers/hooks/use-customer-options';
import { CustomerForm } from 'features/customers';
import { QuickScheduleForm } from './types';
import { useAutoProgressOnProducer } from './use-auto-progress';
import { EnumUserType } from '../../../../lib/graphql';

/** Matches the debounce the cutsheet assignment-rule pickers use. */
const SEARCH_DEBOUNCE_MS = 300;

export function ProducerSelectionStep({
  vendorId,
  onNext = undefined,
}: {
  vendorId: string;
  onNext?: () => void;
}) {
  const canLoadProducers = Boolean(vendorId);

  const formikContext = useFormikContext<QuickScheduleForm>();
  const values = formikContext?.values;

  // Typing re-queries rather than filtering the page already in hand: the
  // resolver caps its result set, so on a vendor with more producers than the
  // cap the unsearched list is a truncated slice and client-side filtering can
  // never reach the producers outside it.
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearch(value), SEARCH_DEBOUNCE_MS),
    [],
  );
  useEffect(
    () => () => {
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch],
  );

  const { customerOptions, loading } = useCustomerOptions(
    vendorId,
    canLoadProducers,
    {
      includeAddNew: false,
      // Deliberately unfiltered by relationship type: a customer recorded as a
      // buyer may still be the one bringing the animals in, so narrowing to
      // `producer` relationships would hide people processors legitimately
      // book for. The server-side `search` below is what keeps the list
      // reachable now, rather than the relationship filter.
      search,
      pinnedId: values?.requesterContactInformation?.producer,
    },
  );

  // Use local state for UI concern, but check if manual producer data exists to persist the state
  const hasManualProducerData = Boolean(
    values?.requesterContactInformation?.first_name ||
      values?.requesterContactInformation?.email ||
      values?.requesterContactInformation?.phone,
  );
  const [showAddProducer, setShowAddProducer] = useState(hasManualProducerData);

  // Keep showAddProducer in sync if manual data exists (e.g., navigating back)
  const prevHasManualDataRef = useRef(hasManualProducerData);
  useEffect(() => {
    if (hasManualProducerData && !prevHasManualDataRef.current) {
      setShowAddProducer(true);
    }
    prevHasManualDataRef.current = hasManualProducerData;
  }, [hasManualProducerData]);

  // Auto-progress when producer is selected from dropdown
  useAutoProgressOnProducer(onNext);

  if (!values) {
    return <div>Loading...</div>;
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <InputTypeahead
        label="Select Producer"
        nameOverride="requesterContactInformation.producer"
        options={customerOptions}
        idKey="_id"
        labelKey="fullName"
        size="medium"
        onInputChange={debouncedSetSearch}
        isLoading={loading}
        disabled={showAddProducer || !canLoadProducers}
      />

      <MuiButton
        variant="text"
        startIcon={<Add />}
        fullWidth
        onClick={() => setShowAddProducer(!showAddProducer)}
        disabled={showAddProducer}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add New Producer
      </MuiButton>

      {showAddProducer && (
        <Box sx={{ mt: 2 }}>
          <CustomerForm
            vendorId={vendorId}
            forcedType={EnumUserType.Producer}
            onCancel={() => setShowAddProducer(false)}
            showActions={false}
            prefix="requesterContactInformation"
            renderAsDiv
          />
        </Box>
      )}
    </Stack>
  );
}
